import { getAuthToken, getCurrentOrg } from "./storage";

// Every call is same-origin (Vite dev/preview proxies /api + /join to the
// real API — see vite.config.ts) so `credentials: "include"` is mostly belt
// and braces here, but it's what makes the trim_session cookie ride along
// when the app IS deployed against a cross-origin API.
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const orgId = getCurrentOrg();
  const token = orgId !== null ? getAuthToken(orgId) : null;
  if (token) headers["X-Trim-Token"] = token;
  return headers;
}

function request(path: string, init: RequestInit): Promise<Response> {
  return fetch(path, { ...init, credentials: "include" });
}

type FormFields = Record<string, string | number | boolean | null | undefined>;

function toParams(fields: FormFields): URLSearchParams {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(fields)) {
    if (v !== null && v !== undefined && v !== "") params.set(k, String(v));
  }
  return params;
}

export const api = {
  get(path: string): Promise<Response> {
    return request(path, { headers: authHeaders() });
  },
  postJson(path: string, body: unknown): Promise<Response> {
    return request(path, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    });
  },
  postForm(path: string, fields: FormFields): Promise<Response> {
    return request(path, { method: "POST", headers: authHeaders(), body: toParams(fields) });
  },
  // No Content-Type here — the browser sets the multipart boundary itself
  // when the body is a FormData instance (scale-cam frames, M5c).
  postMultipart(path: string, form: FormData): Promise<Response> {
    return request(path, { method: "POST", headers: authHeaders(), body: form });
  },
  // Raw PUT for the resumable /api/photos/:id upload — caller supplies
  // Content-Type/Content-Range/X-Content-SHA256/X-Photo-Purpose itself
  // (see lib/photos.ts) since those vary per chunk/purpose.
  putRaw(path: string, body: BodyInit, headers: Record<string, string>): Promise<Response> {
    return request(path, { method: "PUT", headers: authHeaders(headers), body });
  },
  // Added for M5b (manager surfaces): PATCH /api/runs/:id and
  // PATCH /api/jobs/:id both read `request.get_json(silent=True) or
  // request.form` server-side, so a form-encoded body works for either.
  patchForm(path: string, fields: FormFields): Promise<Response> {
    return request(path, { method: "PATCH", headers: authHeaders(), body: toParams(fields) });
  },
  patchJson(path: string, body: unknown): Promise<Response> {
    return request(path, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(body) });
  },
  del(path: string): Promise<Response> {
    return request(path, { method: "DELETE", headers: authHeaders() });
  },
};

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    const msg = body && typeof body === "object" && "error" in (body as Record<string, unknown>)
      ? String((body as Record<string, unknown>).error)
      : `HTTP ${status}`;
    super(msg);
    this.status = status;
    this.body = body;
  }
}

export async function readJson<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}
